import * as yup from 'yup';

/**
 * Utilities for comma-aware numeric form fields (US display, US/EU paste).
 */

const THOUSANDS_GROUP = /^\d{1,3}(,\d{3})+$/;
const EU_THOUSANDS_GROUP = /^\d{1,3}(\.\d{3})+$/;

/**
 * Detect whether a lone comma-separated value uses comma as thousands grouping.
 * @param {string} s
 * @returns {boolean}
 */
function isCommaThousandsPattern(s) {
  return THOUSANDS_GROUP.test(s) || /^\d+,\d{3}$/.test(s);
}

/**
 * Detect whether a lone dot-separated value uses dot as thousands grouping (EU).
 * @param {string} s
 * @returns {boolean}
 */
function isDotThousandsPattern(s) {
  return EU_THOUSANDS_GROUP.test(s) || /^\d+\.\d{3}$/.test(s);
}

/**
 * Normalize a numeric string for parsing (strip spaces, unify separators).
 * @param {string} s
 * @returns {string}
 */
function normalizeForParse(s) {
  return String(s).trim().replace(/\s/g, '');
}

/**
 * Parse user input accepting US and European formats.
 *
 * @param {string|number|null|undefined} value
 * @param {Object} [options]
 * @param {boolean} [options.integerOnly=false]
 * @returns {number|null} Parsed finite number, or null if empty/invalid.
 */
export function parseNumericInput(value, options = {}) {
  const { integerOnly = false } = options;

  if (value === null || value === undefined) return null;
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return null;
    return integerOnly ? Math.trunc(value) : value;
  }

  const raw = normalizeForParse(value);
  if (raw === '' || raw === '-' || raw === '+' || raw === '.' || raw === ',') {
    return null;
  }

  const hasComma = raw.includes(',');
  const hasDot = raw.includes('.');

  let normalized = raw;

  if (hasComma && hasDot) {
    const lastComma = raw.lastIndexOf(',');
    const lastDot = raw.lastIndexOf('.');
    if (lastComma > lastDot) {
      normalized = raw.replace(/\./g, '').replace(',', '.');
    } else {
      normalized = raw.replace(/,/g, '');
    }
  } else if (hasComma) {
    if (isCommaThousandsPattern(raw)) {
      normalized = raw.replace(/,/g, '');
    } else {
      normalized = raw.replace(',', '.');
    }
  } else if (hasDot) {
    if (isDotThousandsPattern(raw)) {
      normalized = raw.replace(/\./g, '');
    }
  }

  const n = Number(normalized);
  if (!Number.isFinite(n)) return null;
  return integerOnly ? Math.trunc(n) : n;
}

/**
 * Format a value for display using en-US grouping (e.g. 10,000.50).
 *
 * @param {string|number|null|undefined} value
 * @param {Object} [options]
 * @param {boolean} [options.integerOnly=false]
 * @param {number} [options.minimumFractionDigits=0]
 * @param {number} [options.maximumFractionDigits=2]
 * @returns {string}
 */
export function formatNumericInput(value, options = {}) {
  const {
    integerOnly = false,
    minimumFractionDigits = 0,
    maximumFractionDigits = integerOnly ? 0 : 2,
  } = options;

  const parsed = parseNumericInput(value, { integerOnly });
  if (parsed === null) return '';

  return parsed.toLocaleString('en-US', {
    minimumFractionDigits: integerOnly ? 0 : minimumFractionDigits,
    maximumFractionDigits: integerOnly ? 0 : maximumFractionDigits,
  });
}

/**
 * Validate numeric input for forms and OCR review.
 *
 * @param {string|number|null|undefined} value
 * @param {Object} [options]
 * @param {boolean} [options.required=false]
 * @param {boolean} [options.integerOnly=false]
 * @param {number} [options.min]
 * @param {number} [options.max]
 * @returns {boolean}
 */
export function isValidNumericInput(value, options = {}) {
  const { required = false, integerOnly = false, min, max } = options;

  const raw = value === null || value === undefined ? '' : String(value).trim();
  if (raw === '') return !required;

  const n = parseNumericInput(value, { integerOnly });
  if (n === null) return false;
  if (integerOnly && !Number.isInteger(n)) return false;
  if (min != null && n < min) return false;
  if (max != null && n > max) return false;
  return true;
}

/**
 * Light sanitization while typing: digits, commas, dots, optional leading minus.
 *
 * @param {string} value
 * @param {boolean} [integerOnly=false]
 * @returns {string}
 */
export function sanitizeNumericTyping(value, integerOnly = false) {
  if (value == null) return '';
  let s = String(value);
  if (integerOnly) {
    s = s.replace(/[^\d,]/g, '');
  } else {
    s = s.replace(/[^\d,.-]/g, '');
    const parts = s.split(/[.,]/);
    if (parts.length > 2) {
      const lastSep = Math.max(s.lastIndexOf(','), s.lastIndexOf('.'));
      s = s.slice(0, lastSep) + s.slice(lastSep).replace(/[.,]/g, '');
    }
  }
  return s;
}

/**
 * Yup string schema for numeric fields stored as strings in Formik.
 *
 * @param {Object} [opts]
 * @param {boolean} [opts.required=true]
 * @param {boolean} [opts.integerOnly=false]
 * @param {number} [opts.min]
 * @param {number} [opts.max]
 * @param {string} [opts.message]
 * @returns {import('yup').StringSchema}
 */
export function yupNumericString(opts = {}) {
  const {
    required = true,
    integerOnly = false,
    min,
    max,
    message = integerOnly
      ? 'Must be a valid whole number'
      : 'Must be a valid number',
  } = opts;

  let schema = yup.string();

  if (required) {
    schema = schema.required(message);
  } else {
    schema = schema.notRequired().nullable();
  }

  return schema.test('numeric-input', message, function (value) {
    const raw = value === null || value === undefined ? '' : String(value).trim();
    if (raw === '') return !required;
    if (!isValidNumericInput(value, { integerOnly, min, max })) return false;
    return true;
  });
}

/**
 * Yup number schema with comma-aware transform (for amount fields).
 *
 * @param {Object} [opts]
 * @param {boolean} [opts.required=true]
 * @param {boolean} [opts.integerOnly=false]
 * @param {number} [opts.min]
 * @param {number} [opts.max]
 * @param {string} [opts.typeError='Enter a valid number']
 * @returns {import('yup').NumberSchema}
 */
export function yupNumericField(opts = {}) {
  const {
    required = true,
    integerOnly = false,
    min,
    max,
    typeError = 'Enter a valid number',
  } = opts;

  let schema = yup
    .number()
    .transform((value, originalValue) => {
      if (originalValue === '' || originalValue === null || originalValue === undefined) {
        return null;
      }
      const parsed = parseNumericInput(originalValue, { integerOnly });
      return parsed === null ? NaN : parsed;
    })
    .typeError(typeError);

  if (required) {
    schema = schema.required(typeError);
  } else {
    schema = schema.nullable();
  }

  if (min != null) schema = schema.min(min, `Must be at least ${min}`);
  if (max != null) schema = schema.max(max, `Must be at most ${max}`);
  if (integerOnly) schema = schema.integer('Must be a whole number');

  return schema;
}
